import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import omitBy from 'lodash/omitBy';
import { v4 as uuid } from 'uuid';

import { Input, MappingSetState, PackCalculatorState } from '../components/shared/types';
import { BIGGEST_PACKS, EVEN_PACKS } from '../reducers/editConstants';
import { ShreddingStrategy } from '../services/types';

type Formula = {
  value: string;
  unit: string;
};
type DimensionFormulas = {
  height?: Formula;
  diameter?: Formula;
  length?: Formula;
  width?: Formula;
  weight?: Formula;
};
export type PackCalculatorRequest = DimensionFormulas & {
  name: string;
  accountId: string;
  inputs: Input[];
  packableQuantities?: number[];
  maxQuantity?: number;
  minQuantity?: number;
  quantityIncrement?: number;
  shreddingStrategy?: ShreddingStrategy;
  preferredContainerType: string;
  packsPerContainer: string;
  etag?: string;
  coamGroupId?: string;
  key?: string;
};

export type TestPackCalculatorRequest = {
  key: string;
  skus: string[];
  _links?: { attributeModel: { href: string } };
  _embedded?: { packCalculator: PackCalculatorRequest; packCalculatorList?: PackCalculatorRequest[] };
  packGroupName?: string;
};

export type TestPackCalculatorMappingSet = {
  accountId: string;
  mappings: TestPackCalculatorRequest[];
};

export const createTestEvaluateRequestFromState = (
  accountId: string,
  mappingSetState: MappingSetState,
): { request: TestPackCalculatorMappingSet; keyMap: Map<string, string[]> } => {
  const keyMap = new Map<string, string[]>(); // mappingKey -> [mainKey, ...listKeys]

  const mappings = mappingSetState.mappings.map((mapping, mappingIndex) => {
    const packCalculatorState = mapping.packCalculator;
    const formulas = createFormulas(packCalculatorState);

    const packableQuantities =
      packCalculatorState.packableQuantities
        ?.split(',')
        .filter(quantity => quantity !== '')
        .map(Number) ?? [];

    const preferredPackQuantity = packCalculatorState.preferredPackQuantity
      ? Number(packCalculatorState.preferredPackQuantity)
      : undefined;

    const mainPackKey = uuid();
    const keys = [mainPackKey]; // Track all keys for this mapping

    const packCalculator: PackCalculatorRequest = removeNullFields({
      name: packCalculatorState.name,
      accountId,
      inputs: packCalculatorState.inputs,
      packableQuantities,
      preferredPackQuantity,
      preferredContainerType: packCalculatorState.preferredContainerType,
      packsPerContainer: packCalculatorState.packsPerContainer,
      etag: packCalculatorState.etag,
      key: mainPackKey,
      ...formulas,
    });

    if (!isEmpty(packCalculatorState.packableQuantities)) {
      packCalculator.packableQuantities = packCalculatorState.packableQuantities?.split(',').map(Number);
      packCalculator.shreddingStrategy = BIGGEST_PACKS;
    }

    if (!isEmpty(packCalculatorState.maxQuantity)) {
      packCalculator.maxQuantity = Number(packCalculatorState.maxQuantity);
      packCalculator.minQuantity = 1;
      packCalculator.quantityIncrement = 1;
      packCalculator.shreddingStrategy = EVEN_PACKS;
    }

    // Process pack calculator list if it exists
    let packCalculatorList: PackCalculatorRequest[] | undefined;
    if (mapping.packCalculatorList && mapping.packCalculatorList.length > 0) {
      packCalculatorList = mapping.packCalculatorList.map((calculatorState, index) => {
        const listFormulas = createFormulas(calculatorState);

        const listPackableQuantities =
          calculatorState.packableQuantities
            ?.split(',')
            .filter(quantity => quantity !== '')
            .map(Number) ?? [];

        const listPreferredPackQuantity = calculatorState.preferredPackQuantity
          ? Number(calculatorState.preferredPackQuantity)
          : undefined;

        const listPackKey = uuid();
        keys.push(listPackKey);

        const listPackCalculator: PackCalculatorRequest = removeNullFields({
          name: calculatorState.name || 'Pack',
          accountId,
          inputs: calculatorState.inputs || [],
          packableQuantities: listPackableQuantities,
          preferredPackQuantity: listPreferredPackQuantity,
          preferredContainerType: calculatorState.preferredContainerType || '',
          packsPerContainer: calculatorState.packsPerContainer || '',
          etag: calculatorState.etag,
          key: listPackKey,
          ...listFormulas,
        });

        if (!isEmpty(calculatorState.packableQuantities)) {
          listPackCalculator.packableQuantities = calculatorState.packableQuantities?.split(',').map(Number);
          listPackCalculator.shreddingStrategy = BIGGEST_PACKS;
        }

        if (!isEmpty(calculatorState.maxQuantity)) {
          listPackCalculator.maxQuantity = Number(calculatorState.maxQuantity);
          listPackCalculator.minQuantity = 1;
          listPackCalculator.quantityIncrement = 1;
          listPackCalculator.shreddingStrategy = EVEN_PACKS;
        }

        return listPackCalculator;
      });
    }

    const testPackCalculatorRequest: TestPackCalculatorRequest = {
      key: mapping.id,
      skus: mapping.skus,
      _embedded: packCalculatorList ? { packCalculator, packCalculatorList } : { packCalculator },
    };

    if (packCalculatorList && mapping.packGroupName) {
      testPackCalculatorRequest.packGroupName = mapping.packGroupName;
    }

    if (mapping.attributeModelUrl) {
      testPackCalculatorRequest._links = { attributeModel: { href: mapping.attributeModelUrl } };
    }

    keyMap.set(String(mappingIndex), keys);

    return testPackCalculatorRequest;
  });

  return {
    request: { accountId, mappings },
    keyMap,
  };
};

export const createMappingSetRequestFromState = ({
  accountId,
  mappingSetState,
}: {
  accountId: string;
  mappingSetState: MappingSetState;
}) => {
  const mappings = mappingSetState.mappings.map(mapping => {
    const packCalculatorState = mapping.packCalculator;
    const formulas = createFormulas(packCalculatorState);

    const packableQuantities =
      packCalculatorState.packableQuantities
        ?.split(',')
        .filter(quantity => quantity !== '')
        .map(Number) ?? [];

    const preferredPackQuantity = packCalculatorState.preferredPackQuantity
      ? Number(packCalculatorState.preferredPackQuantity)
      : undefined;

    const packCalculator: PackCalculatorRequest = removeNullFields({
      name: packCalculatorState.name,
      accountId,
      inputs: packCalculatorState.inputs,
      packableQuantities,
      preferredPackQuantity,
      preferredContainerType: packCalculatorState.preferredContainerType,
      packsPerContainer: packCalculatorState.packsPerContainer,
      etag: packCalculatorState.etag,
      ...formulas,
    });

    if (!isEmpty(packCalculatorState.packableQuantities)) {
      packCalculator.packableQuantities = packCalculatorState.packableQuantities?.split(',').map(Number);
      packCalculator.shreddingStrategy = BIGGEST_PACKS;
    }

    if (!isEmpty(packCalculatorState.maxQuantity)) {
      packCalculator.maxQuantity = Number(packCalculatorState.maxQuantity);
      packCalculator.minQuantity = 1;
      packCalculator.quantityIncrement = 1;
      packCalculator.shreddingStrategy = EVEN_PACKS;
    }

    // Process pack calculator list if it exists
    let packCalculatorList: PackCalculatorRequest[] | undefined;
    if (mapping.packCalculatorList && mapping.packCalculatorList.length > 0) {
      packCalculatorList = mapping.packCalculatorList.map(calculatorState => {
        const listFormulas = createFormulas(calculatorState);

        const listPackableQuantities =
          calculatorState.packableQuantities
            ?.split(',')
            .filter(quantity => quantity !== '')
            .map(Number) ?? [];

        const listPackCalculator: PackCalculatorRequest = removeNullFields({
          name: calculatorState.name || 'Pack',
          accountId,
          inputs: calculatorState.inputs || [],
          packableQuantities: listPackableQuantities,
          preferredContainerType: calculatorState.preferredContainerType || '',
          packsPerContainer: calculatorState.packsPerContainer || '',
          etag: calculatorState.etag,
          ...listFormulas,
        });

        if (!isEmpty(calculatorState.packableQuantities)) {
          listPackCalculator.packableQuantities = calculatorState.packableQuantities?.split(',').map(Number);
          listPackCalculator.shreddingStrategy = BIGGEST_PACKS;
        }

        if (!isEmpty(calculatorState.maxQuantity)) {
          listPackCalculator.maxQuantity = Number(calculatorState.maxQuantity);
          listPackCalculator.minQuantity = 1;
          listPackCalculator.quantityIncrement = 1;
          listPackCalculator.shreddingStrategy = EVEN_PACKS;
        }

        return listPackCalculator;
      });
    }

    const result: any = {
      skus: mapping.skus,
      attributeModelUrl: mapping.attributeModelUrl,
      packCalculatorUrl: mapping.packCalculatorUrl,
      packCalculatorId: mapping.retrievedPackCalculatorId,
    };

    // Add packCalculatorList to the result if it exists
    if (packCalculatorList !== undefined) {
      result.packCalculatorList = packCalculatorList;

      if (mapping.packGroupName) {
        result.packGroupName = mapping.packGroupName;
      }
    } else {
      // Only include packCalculator when there's no packCalculatorList
      result.packCalculator = packCalculator;
    }

    return result;
  });

  return {
    name: mappingSetState.name,
    mappings,
    etag: mappingSetState.etag,
  };
};

const createFormulas = (packCalculatorState: PackCalculatorState) => {
  const formulas: DimensionFormulas = {};

  if (packCalculatorState.preferredContainerType === 'tube') {
    if (packCalculatorState.heightFormula) {
      formulas.height = {
        value: packCalculatorState.heightFormula,
        unit: packCalculatorState.dimensionUnit,
      };
    }

    if (packCalculatorState.diameterFormula) {
      formulas.diameter = {
        value: packCalculatorState.diameterFormula,
        unit: packCalculatorState.dimensionUnit,
      };
    }
  } else {
    if (packCalculatorState.lengthFormula) {
      formulas.length = {
        value: packCalculatorState.lengthFormula,
        unit: packCalculatorState.dimensionUnit,
      };
    }

    if (packCalculatorState.widthFormula) {
      formulas.width = {
        value: packCalculatorState.widthFormula,
        unit: packCalculatorState.dimensionUnit,
      };
    }

    if (packCalculatorState.heightFormula) {
      formulas.height = {
        value: packCalculatorState.heightFormula,
        unit: packCalculatorState.dimensionUnit,
      };
    }
  }

  if (packCalculatorState.weightFormula) {
    formulas.weight = {
      value: packCalculatorState.weightFormula,
      unit: packCalculatorState.weightUnit,
    };
  }

  return formulas;
};

const removeNullFields = <T>(obj: T): T => omitBy(obj, isNil) as T;
